import { useCallback, useMemo, useState } from 'react';

import { format } from 'date-fns';
import { CalendarIcon, Info } from 'lucide-react';
import { Control, FieldValues, Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { SimpleTooltip } from '@/components/ui/simple-tooltip';
import { cn } from '@/lib/utils';
import { MonthDay } from '@/modules/common/types/month-day';
import { formatShortDate } from '@/utils/date-formatters';

// Use a fixed leap year so Feb 29 is always selectable
const FIXED_YEAR = 2024;

const toDate = (monthDay: MonthDay): Date => new Date(FIXED_YEAR, monthDay.month - 1, monthDay.day);

const fromDate = (date: Date): MonthDay => ({
  month: date.getMonth() + 1,
  day: date.getDate(),
});

interface MonthDayPickerProps<T extends FieldValues> {
  name: Path<T>;
  control: Control<T>;
  label?: string;
  tooltip?: string;
  disabled?: boolean;
  displayValueOverride?: string;
}

export const MonthDayPicker = <T extends FieldValues>({
  name,
  control,
  label,
  tooltip,
  disabled = false,
  displayValueOverride,
}: MonthDayPickerProps<T>) => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation('common');

  const formatDisplay = useCallback(
    (monthDay: MonthDay): string => {
      const date = toDate(monthDay);
      return `${formatShortDate(date)} ${t('monthDayPicker.eachYearSuffix')}`;
    },
    [t]
  );

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        const selectedDate = field.value ? toDate(field.value as MonthDay) : undefined;

        return (
          <FormItem className="flex flex-col">
            {label && (
              <div className="flex items-center gap-1.5">
                <FormLabel>{label}</FormLabel>
                {tooltip && (
                  <SimpleTooltip
                    content={<p className="text-xs">{tooltip}</p>}
                    contentClassName="max-w-xs"
                  >
                    <Info className="h-3.5 w-3.5 cursor-help text-muted-foreground" />
                  </SimpleTooltip>
                )}
              </div>
            )}

            <Popover open={isOpen} onOpenChange={setIsOpen}>
              <PopoverTrigger asChild>
                <FormControl>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={disabled}
                    className={cn(
                      'w-full justify-start text-left font-normal',
                      !field.value && 'text-muted-foreground',
                      'disabled:border-input disabled:opacity-100 disabled:bg-muted disabled:pointer-events-none'
                    )}
                  >
                    <span className="flex-1">
                      {displayValueOverride ??
                        (field.value
                          ? formatDisplay(field.value as MonthDay)
                          : t('monthDayPicker.placeholder'))}
                    </span>
                    <CalendarIcon className="ml-2 h-4 w-4 flex-shrink-0 opacity-50" />
                  </Button>
                </FormControl>
              </PopoverTrigger>

              <PopoverContent className="w-auto p-0" align="start" side="bottom">
                <MonthDayCalendar
                  selected={selectedDate}
                  onSelect={date => {
                    if (date) {
                      field.onChange(fromDate(date));
                    }
                    setIsOpen(false);
                  }}
                />
              </PopoverContent>
            </Popover>

            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
};

interface MonthDayCalendarProps {
  selected?: Date;
  onSelect: (date: Date | undefined) => void;
}

const MonthDayCalendar = ({ selected, onSelect }: MonthDayCalendarProps) => {
  const [month, setMonth] = useState<Date>(selected ?? new Date(FIXED_YEAR, 0, 1));

  const displayMonth = useMemo(() => new Date(FIXED_YEAR, month.getMonth(), 1), [month]);

  const handleMonthChange = useCallback((newMonth: Date) => {
    setMonth(new Date(FIXED_YEAR, newMonth.getMonth(), 1));
  }, []);

  return (
    <Calendar
      mode="single"
      selected={selected}
      onSelect={onSelect}
      month={displayMonth}
      onMonthChange={handleMonthChange}
      showOutsideDays={true}
      fixedWeeks
      formatters={{
        formatCaption: date => format(date, 'MMMM'),
      }}
    />
  );
};
